import { useMemo } from 'react';
import { useFileOperations } from '../files/useFileOperations';
import { useDealOperations } from '../deals/useDealOperations';
import type { DealWithTitle } from 'src/types/deal.types';
import type {
  VaultFileItem,
  VaultFolderNode,
  VaultDeal,
  FileIconType,
  CreateDealInput,
  CreateDealResult,
  CreateFileInput,
} from 'src/types/vault.types';
import type {
  File as ApiFile,
  UpdateFileRequestContent,
  UpdateDealRequestContent,
  GetDealResponseContent,
} from '@equaliq/api-model-v2';

interface FileMetadataWithFolderPath {
  folderPath?: string;
}

interface FileWithDealIds extends ApiFile {
  dealIds?: string[];
}

function mimeToFileIcon(fileType: string): FileIconType {
  const lower = fileType.toLowerCase();

  if (lower.includes('pdf')) return 'pdf';
  if (lower.includes('word') || lower.includes('docx') || lower.includes('doc')) return 'docx';
  if (
    lower.includes('sheet') ||
    lower.includes('xlsx') ||
    lower.includes('xls') ||
    lower.includes('csv')
  ) {
    return 'xlsx';
  }
  if (
    lower.includes('image') ||
    lower.includes('png') ||
    lower.includes('jpg') ||
    lower.includes('jpeg') ||
    lower.includes('gif') ||
    lower.includes('webp')
  ) {
    return 'image';
  }
  if (lower.includes('http')) return 'link';

  return 'text';
}

function formatDate(isoDate?: string): string {
  if (!isoDate) {
    return '';
  }

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return isoDate;
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** i;
  return `${i === 0 ? value : value.toFixed(1)} ${units[i]}`;
}

function getExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot >= 0 ? fileName.slice(dot).toLowerCase() : '';
}

function apiFileToVaultItem(file: ApiFile): VaultFileItem {
  const metadata = file.metadata as FileMetadataWithFolderPath | undefined;
  return {
    id: file.fileId,
    name: file.fileName,
    fileIcon: mimeToFileIcon(file.fileType || ''),
    date: formatDate(file.updatedAt || file.createdAt),
    sizeBytes: file.sizeBytes ?? 0,
    sizeFormatted: formatFileSize(file.sizeBytes ?? 0),
    fileType: file.fileType || 'application/octet-stream',
    extension: getExtension(file.fileName),
    description: file.description,
    createdAt: file.createdAt,
    updatedAt: file.updatedAt,
    createdAtFormatted: formatDate(file.createdAt),
    updatedAtFormatted: formatDate(file.updatedAt),
    folderPath: metadata?.folderPath,
  };
}

function buildFileTree(files: ApiFile[]): VaultFolderNode {
  const root: VaultFolderNode = { id: 'eq-root', name: 'EQUAL-IQ', children: [] };
  const folderMap = new Map<string, VaultFolderNode>();
  folderMap.set('', root);

  for (const file of files) {
    const metadata = file.metadata as FileMetadataWithFolderPath | undefined;
    const folderPath = metadata?.folderPath || '';

    if (!folderPath) {
      root.children.push(apiFileToVaultItem(file));
      continue;
    }

    const parts = folderPath.split('/').filter(Boolean);
    let currentPath = '';
    let currentNode = root;

    for (const part of parts) {
      const previousPath = currentPath;
      currentPath = currentPath ? `${currentPath}/${part}` : part;

      if (!folderMap.has(currentPath)) {
        const folder: VaultFolderNode = {
          id: `folder-${currentPath}`,
          name: part,
          children: [],
        };
        folderMap.set(currentPath, folder);
        const parentNode = folderMap.get(previousPath) || root;
        parentNode.children.push(folder);
      }

      currentNode = folderMap.get(currentPath) || currentNode;
    }

    currentNode.children.push(apiFileToVaultItem(file));
  }

  return root;
}

function apiDealToVaultDeal(deal: DealWithTitle): VaultDeal {
  return {
    id: deal.dealId,
    name: deal.title,
    type: deal.latestStage || 'drafting',
    status: deal.latestStage || 'drafting',
  };
}

interface UseVaultDataResult {
  fileTree: VaultFolderNode;
  deals: VaultDeal[];
  dealFilesMap: Record<string, VaultFileItem[]>;
  isLoading: boolean;
  error: unknown;
  createFile: (input: CreateFileInput) => Promise<void>;
  updateFile: (request: UpdateFileRequestContent) => Promise<void>;
  deleteFile: (fileId: string) => Promise<void>;
  refreshFiles: () => Promise<void>;
  createDeal: (input: CreateDealInput) => Promise<CreateDealResult>;
  updateDeal: (request: UpdateDealRequestContent) => Promise<void>;
  deleteDeal: (dealId: string) => Promise<void>;
  refreshDeals: () => Promise<void>;
  getDeal: (dealId: string) => Promise<GetDealResponseContent>;
}

export const useVaultData = (orgId?: string): UseVaultDataResult => {
  const fileOps = useFileOperations(orgId);
  const dealOps = useDealOperations(orgId);

  const fileTree = useMemo(() => buildFileTree(fileOps.files), [fileOps.files]);

  const deals = useMemo(() => dealOps.deals.map(apiDealToVaultDeal), [dealOps.deals]);

  const dealFilesMap = useMemo(() => {
    const map: Record<string, VaultFileItem[]> = {};

    for (const file of fileOps.files) {
      const fileWithDealIds = file as FileWithDealIds;
      const dealIds = fileWithDealIds.dealIds || [];

      for (const dealId of dealIds) {
        if (!map[dealId]) {
          map[dealId] = [];
        }

        map[dealId].push(apiFileToVaultItem(file));
      }
    }

    return map;
  }, [fileOps.files]);

  const createFileWrapped = async (input: CreateFileInput): Promise<void> => {
    await fileOps.createFile(input);
  };

  const createDealWrapped = async (input: CreateDealInput): Promise<CreateDealResult> => {
    const response = await dealOps.createDeal(input);
    return { dealId: response.deal.dealId };
  };

  return {
    fileTree,
    deals,
    dealFilesMap,
    isLoading: fileOps.isLoading || dealOps.isLoading,
    error: fileOps.error || dealOps.error,
    createFile: createFileWrapped,
    updateFile: fileOps.updateFile,
    deleteFile: fileOps.deleteFile,
    refreshFiles: fileOps.refreshFiles,
    createDeal: createDealWrapped,
    updateDeal: dealOps.updateDeal,
    deleteDeal: dealOps.deleteDeal,
    refreshDeals: dealOps.refreshDeals,
    getDeal: dealOps.getDeal,
  };
};
